import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { brandInfo } from '@/mocks/brand';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q1euue3oq4jqumtf7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100/60">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">关于我们</span>
              </nav>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-3">
                关于爱液网页版
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl">
                {brandInfo.description}
              </p>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {brandInfo.mission}
                </p>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  我们的愿景
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {brandInfo.vision}
                </p>
              </div>
              <div className="relative h-72 md:h-80 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Diverse%20creative%20team%20collaborating%20in%20a%20warm%20modern%20office%20with%20large%20windows%20and%20natural%20light%2C%20minimalist%20workspace%20design%2C%20friendly%20professional%20atmosphere%2C%20editorial%20photography%20style%2C%20warm%20neutral%20tones&width=800&height=500&seq=about-team-aiye-01&orientation=landscape"
                  alt="爱液网页版团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-16 bg-background-100/60">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  核心价值观
                </h2>
                <p className="text-sm text-foreground-600">驱动爱液网页版不断前进的信念</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {brandInfo.values.map((v) => (
                  <div key={v.title} className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="flex flex-col gap-6">
                  {brandInfo.milestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`relative flex items-start gap-6 md:gap-10 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                        <div className="hidden md:block">
                          <span className="text-sm font-semibold text-primary-600">{milestone.year}</span>
                          <p className="text-sm text-foreground-700 mt-1">{milestone.event}</p>
                        </div>
                        <div className="md:hidden">
                          <span className="text-sm font-semibold text-primary-600">{milestone.year}</span>
                          <p className="text-sm text-foreground-700 mt-1">{milestone.event}</p>
                        </div>
                      </div>
                      <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-primary-100 border-2 border-primary-300 flex items-center justify-center md:-translate-x-1/2 z-10">
                        <div className="w-2.5 h-2.5 rounded-full bg-primary-500"></div>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-16 bg-background-100/60">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">{brandInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">客服电话</p>
                      <p className="text-sm font-medium text-foreground-800">{brandInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">公司地址</p>
                      <p className="text-sm font-medium text-foreground-800">{brandInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">工作时间</p>
                      <p className="text-sm font-medium text-foreground-800">{brandInfo.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-background-50 border border-background-200/60 p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">在线咨询</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="about-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入咨询主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您想咨询的内容（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    className="px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    提交咨询
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">感谢您的咨询，我们会尽快与您联系！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500">{formError || '提交失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于爱液网页版',
              description: brandInfo.description,
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aiye.com' },
                  { '@type': 'ListItem', position: 2, name: '关于我们' },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}